
import { lazy, Suspense } from 'react';
import { RouteObject } from 'react-router-dom';
import { ProtectedRoute } from '../components/guards/ProtectedRoute';
import { SuperAdminRoute } from '../components/guards/SuperAdminRoute';
import PageLoader from '../components/ui/PageLoader';

// Add loading fallback component for better UX
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <PageLoader message="Carregando página..." />
  </div>
);

// Auth pages
const Login = lazy(() => import('../pages/auth/login/page'));
const Register = lazy(() => import('../pages/auth/register/page'));
const PagamentoConfirmado = lazy(() => import('../pages/pagamento-confirmado/page'));


// Super Admin pages
const SuperAdminDashboard = lazy(() => import('../pages/super-admin/dashboard/page'));
const SuperAdminClientes = lazy(() => import('../pages/super-admin/dashboard/clientes'));
const SuperAdminProdutos = lazy(() => import('../pages/super-admin/dashboard/produtos'));
const SuperAdminOrganizations = lazy(() => import('../pages/super-admin/organizations/page'));
const SuperAdminOrganizationDetails = lazy(() => import('../pages/super-admin/organizations/[id]/page'));
const SuperAdminUsers = lazy(() => import('../pages/super-admin/users/page'));
const SuperAdminSettings = lazy(() => import('../pages/super-admin/settings/page'));
const SuperAdminPlans = lazy(() => import('../pages/super-admin/plans/page'));
const SuperAdminEditais = lazy(() => import('../pages/super-admin/editais/page'));
const SuperAdminKnowledgeBase = lazy(() => import('../pages/super-admin/knowledge-base/page'));
const SuperAdminAuditoria = lazy(() => import('../pages/super-admin/auditoria/page'));
const SuperAdminRecargas = lazy(() => import('../pages/super-admin/recargas/page'));
const SuperAdminMovimentacoes = lazy(() => import('../pages/super-admin/movimentacoes/page'));

// App pages
const Layout = lazy(() => import('../components/feature/Layout'));
const Home = lazy(() => import('../pages/home/page'));
const Rastreamento = lazy(() => import('../pages/rastreamento/page'));
const PlacasConsultadas = lazy(() => import('../pages/rastreamento/placas-consultadas/page'));
const StatusRecurso = lazy(() => import('../pages/status-recurso/page'));
const RecursosIA = lazy(() => import('../pages/recursos-ia/page'));
const Planos = lazy(() => import('../pages/planos/page'));
const CRMKanban = lazy(() => import('../pages/atendimento/crm-kanban/page'));
const NovoCliente = lazy(() => import('../pages/cadastro/novo-cliente/page'));
const ListaClientes = lazy(() => import('../pages/cadastro/lista-clientes/page'));
const ProspeccaoEditais = lazy(() => import('../pages/prospeccao-editais/page'));
const Checkout = lazy(() => import('../pages/checkout/page'));
const Perfil = lazy(() => import('../pages/perfil/page'));
const Servicos = lazy(() => import('../pages/servicos/page'));
const NotFound = lazy(() => import('../pages/NotFound'));

// Wrap lazy components in Suspense for error boundaries
const routes: RouteObject[] = [
  // Public routes
  {
    path: '/login',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <Login />
      </Suspense>
    ),
  },
  {
    path: '/register',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <Register />
      </Suspense>
    ),
  },
  {
    path: '/pagamento-confirmado',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <PagamentoConfirmado />
      </Suspense>
    ),
  },

  // Protected routes
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <Layout />
        </Suspense>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Home />
          </Suspense>
        ),
      },
      {
        path: 'rastreamento',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Rastreamento />
          </Suspense>
        ),
      },
      {
        path: 'rastreamento/placas-consultadas',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <PlacasConsultadas />
          </Suspense>
        ),
      },
      {
        path: 'status-recurso',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <StatusRecurso />
          </Suspense>
        ),
      },
      {
        path: 'recursos-ia',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <RecursosIA />
          </Suspense>
        ),
      },
      {
        path: 'planos',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Planos />
          </Suspense>
        ),
      },
      {
        path: 'crm-kanban',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <CRMKanban />
          </Suspense>
        ),
      },
      {
        path: 'cadastro/novo-cliente',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <NovoCliente />
          </Suspense>
        ),
      },
      {
        path: 'cadastro/lista-clientes',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ListaClientes />
          </Suspense>
        ),
      },
      {
        path: 'servicos',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Servicos />
          </Suspense>
        ),
      },
      {
        path: 'prospeccao-editais',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProspeccaoEditais />
          </Suspense>
        ),
      },
      {
        path: 'checkout',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Checkout />
          </Suspense>
        ),
      },
      {
        path: 'perfil',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <Perfil />
          </Suspense>
        ),
      },
      {
        path: 'super-admin',
        element: (
          <SuperAdminRoute>
            <Suspense fallback={<LoadingFallback />}>
              <SuperAdminDashboard />
            </Suspense>
          </SuperAdminRoute>
        ),
      },
      {
        path: 'super-admin/dashboard',
        element: (
          <SuperAdminRoute>
            <Suspense fallback={<LoadingFallback />}>
              <SuperAdminDashboard />
            </Suspense>
          </SuperAdminRoute>
        ),
      },
      {
        path: 'super-admin/dashboard/clientes',
        element: (
          <SuperAdminRoute>
            <Suspense fallback={<LoadingFallback />}>
              <SuperAdminClientes />
            </Suspense>
          </SuperAdminRoute>
        ),
      },
      {
        path: 'super-admin/dashboard/produtos',
        element: (
          <SuperAdminRoute>
            <Suspense fallback={<LoadingFallback />}>
              <SuperAdminProdutos />
            </Suspense>
          </SuperAdminRoute>
        ),
      },
      {
        path: 'super-admin/organizations',
        element: (
          <SuperAdminRoute>
            <Suspense fallback={<LoadingFallback />}>
              <SuperAdminOrganizations />
            </Suspense>
          </SuperAdminRoute>
        ),
      },
      {
        path: 'super-admin/organizations/:id',
        element: (
          <SuperAdminRoute>
            <Suspense fallback={<LoadingFallback />}>
              <SuperAdminOrganizationDetails />
            </Suspense>
          </SuperAdminRoute>
        ),
      },
      {
        path: 'super-admin/users',
        element: (
          <SuperAdminRoute>
            <Suspense fallback={<LoadingFallback />}>
              <SuperAdminUsers />
            </Suspense>
          </SuperAdminRoute>
        ),
      },
      {
        path: 'super-admin/settings',
        element: (
          <SuperAdminRoute>
            <Suspense fallback={<LoadingFallback />}>
              <SuperAdminSettings />
            </Suspense>
          </SuperAdminRoute>
        ),
      },
      {
        path: 'super-admin/plans',
        element: (
          <SuperAdminRoute>
            <Suspense fallback={<LoadingFallback />}>
              <SuperAdminPlans />
            </Suspense>
          </SuperAdminRoute>
        ),
      },
      {
        path: 'super-admin/editais',
        element: (
          <SuperAdminRoute>
            <Suspense fallback={<LoadingFallback />}>
              <SuperAdminEditais />
            </Suspense>
          </SuperAdminRoute>
        ),
      },
      {
        path: 'super-admin/knowledge-base',
        element: (
          <SuperAdminRoute>
            <Suspense fallback={<LoadingFallback />}>
              <SuperAdminKnowledgeBase />
            </Suspense>
          </SuperAdminRoute>
        ),
      },
      {
        path: 'super-admin/auditoria',
        element: (
          <SuperAdminRoute>
            <Suspense fallback={<LoadingFallback />}>
              <SuperAdminAuditoria />
            </Suspense>
          </SuperAdminRoute>
        ),
      },
      {
        path: 'super-admin/recargas',
        element: (
          <SuperAdminRoute>
            <Suspense fallback={<LoadingFallback />}>
              <SuperAdminRecargas />
            </Suspense>
          </SuperAdminRoute>
        ),
      },
      {
        path: 'super-admin/movimentacoes',
        element: (
          <SuperAdminRoute>
            <Suspense fallback={<LoadingFallback />}>
              <SuperAdminMovimentacoes />
            </Suspense>
          </SuperAdminRoute>
        ),
      },
    ],
  },
  {
    path: '*',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <NotFound />
      </Suspense>
    ),
  },
];

export default routes;
